import React, { Component } from "react";
import AreaChart from "../Components/charts/AreaChart";
import Api from "../api";
import Card from "../Components/Card";
import { Spin, Tabs, DatePicker, Button } from "antd";
import GamesPlayedChart from "../Components/GamesPlayedChart";
import { SessionOverview } from "./account/SessionOverview";
import { showError } from "../utils";
import moment, { Moment } from "moment";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

type Preset = "30d" | "12m" | "all" | "custom";
type Range = [Moment, Moment] | null;

const PRESETS: { id: Preset; label: string }[] = [
  { id: "30d", label: "Last 30 days" },
  { id: "12m", label: "Last 12 months" },
  { id: "all", label: "All time" },
];

function rangeForPreset(preset: Preset): Range {
  if (preset === "30d") {
    return [
      moment().subtract(30, "days").startOf("day"),
      moment().endOf("day"),
    ];
  }
  if (preset === "12m") {
    return [
      moment().subtract(12, "months").startOf("day"),
      moment().endOf("day"),
    ];
  }
  return null;
}

function filterByRange(sessions: Session[], range: Range): Session[] {
  if (!range) return sessions;
  const [start, end] = range;
  const startMs = start.valueOf();
  const endMs = end.valueOf();
  return sessions.filter((s) => {
    const ts = new Date(s.timestamp).getTime();
    return ts >= startMs && ts <= endMs;
  });
}

export default class Dashboard extends Component {
  sessions: Session[] = [];
  state = {
    fetching: true,
    hasSessions: false,
    gamesPlayed: undefined,
    averagePerHour: undefined,
    averagePerWeekday: undefined,
    numPlayers: null,
    numGames: null,
    gamesPerHour: null,
    gamesPerDay: null,
    playersPerHour: null,
    playersPerGame: null,
    filteredSessions: [] as Session[],
    loading: false,
    range: rangeForPreset("12m"),
    activePreset: "12m" as Preset,
  };
  componentDidMount() {
    this.fetchGamesPlayed();
  }

  fetchGamesPlayed = async () => {
    try {
      const sessions: Session[] = await Api.get("/api/stats/games_played", {});
      this.sessions = sessions;
      this.updateStats(filterByRange(sessions, this.state.range));
    } catch (error) {
      showError("Failed to get game stats", error);
    }
  };

  applyRange = (range: Range) => {
    const filtered = filterByRange(this.sessions, range);
    this.setState({ filteredSessions: [], loading: true });
    setTimeout(() => this.updateStats(filtered), 50);
  };

  setPreset = (preset: Preset) => {
    const range = rangeForPreset(preset);
    this.setState({ range, activePreset: preset });
    this.applyRange(range);
  };

  handleRangeChange = (
    dates: [Moment | null, Moment | null] | null | undefined
  ) => {
    if (!dates || !dates[0] || !dates[1]) {
      this.setPreset("all");
      return;
    }
    const range: Range = [
      (dates[0] as Moment).startOf("day"),
      (dates[1] as Moment).endOf("day"),
    ];
    this.setState({ range, activePreset: "custom" as Preset });
    this.applyRange(range);
  };

  updateStats = (sessions: Session[]) => {
    if (!sessions) {
      this.setState({ fetching: false, loadding: false });
      return;
    }
    const numPlayers = sessions.reduce(
      (acc, { player_count }) => acc + player_count,
      0
    );
    const numGames = sessions.length;
    const gamesPlayed = getGamesPlayedData(sessions);
    const numberOfDays = gamesPlayed.length;
    const gamesPerDay = sessions.length / numberOfDays || 0;
    const gamesPerHour = gamesPerDay / 24;
    const averagePerHour = getAverageGamesPerHour(sessions);
    const playersPerGame = numPlayers / sessions.length || 0;
    const playersPerHour = gamesPerHour * (numPlayers / sessions.length);
    const averagePerWeekday = getAveraGamesPerWeekDay(sessions, numberOfDays);
    this.setState({
      gamesPlayed,
      averagePerHour,
      numPlayers,
      numGames,
      gamesPerHour,
      gamesPerDay,
      playersPerHour,
      playersPerGame,
      averagePerWeekday,
      fetching: false,
      hasSessions: sessions.length > 0,
      filteredSessions: sessions,
      loading: false,
    });
  };
  render() {
    if (this.state.fetching)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <Spin size="large" />
        </div>
      );

    return (
      <div style={{ width: "100%" }}>
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h2>Dashboard</h2>
            <span>Location performance overview</span>
          </div>
          <div className="dashboard-filters">
            <div className="filter-presets" role="group" aria-label="Date range">
              {PRESETS.map((p) => (
                <Button
                  key={p.id}
                  type={
                    this.state.activePreset === p.id ? "primary" : "default"
                  }
                  onClick={() => this.setPreset(p.id)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <RangePicker
              value={
                this.state.activePreset === "all" ? null : this.state.range
              }
              onChange={this.handleRangeChange}
              allowClear
            />
          </div>
        </div>
        {!this.state.hasSessions && (
          <p style={{ color: "#6b7fa8", fontSize: 14 }}>
            No games played in this period.
          </p>
        )}
        <div
          className="dashboard-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "240px 240px 1fr",
            gap: "20px",
            gridAutoRows: "minmax(50px, auto)",
            width: "100%",
          }}
        >
          <Card title="PLAYERS">{<h1>{round(this.state.numPlayers)}</h1>}</Card>

          <Card title="GAMES PLAYED">
            {<h1>{round(this.state.numGames)}</h1>}
          </Card>
          <Card title="GAMES PER DAY">
            {<h1>{round(this.state.gamesPerDay)}</h1>}
          </Card>

          {/* <Card title="PLAYERS PER HOUR">{<h1>{this.state.playersPerHour}</h1>}</Card> */}
          <Card title="PLAYERS PER GAME">
            {<h1>{round(this.state.playersPerGame)}</h1>}
          </Card>

          {/* <Card title="GAMES PER HOUR">{<h1>{this.state.gamesPerHour}</h1>}</Card> */}
          <Card style={{ gridRow: "1/4", gridColumn: "3/4", width: "100%", textAlign: "left" }}>
            <Tabs defaultActiveKey="1">
              <TabPane tab="GAMES PLAYED" key="1">
                <AreaChart
                  loading={this.state.loading}
                  interpolation="linear"
                  data={this.state.gamesPlayed}
                  allowZoom={true}
                />
              </TabPane>
              <TabPane tab="AVERAGE PER HOUR" key="2">
                <AreaChart
                  loading={this.state.loading}
                  data={this.state.averagePerHour}
                  useHours
                  fixLabelOverlap={false}
                  allowZoom={false}
                  interpolation="linear"
                  tickValues={hoursTicks}
                />
              </TabPane>
              <TabPane tab="AVERAGE PER DAY" key="3">
                <AreaChart
                  loading={this.state.loading}
                  data={this.state.averagePerWeekday}
                  allowZoom={false}
                  tickValues={weekDays}
                  interpolation="linear"
                />
              </TabPane>
            </Tabs>
          </Card>
          <Card style={{ gridRow: "4/4", gridColumn: "1/4", width: "100%", textAlign: "left" }}>
            <GamesPlayedChart
              loading={this.state.loading}
              sessions={this.state.filteredSessions}
            />
          </Card>
          <div
            className="dashboard-session-overview"
            style={{ gridRow: "5/5", gridColumn: "1/4", width: "100%" }}
          >
            <SessionOverview
              sessions={this.state.filteredSessions}
              description="Every game session played in the selected date range"
            />
          </div>
          <svg style={{ width: 0, height: 0, position: "absolute" }}>
            <defs>
              <linearGradient
                id="strokeGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#4f87eb" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
              <linearGradient
                id="fillGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#4f87eb" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0.15" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    );
  }
}

function getAveraGamesPerWeekDay(sessions: Session[], numberOfDays: number) {
  const gamesPlayedByDay: GamesPlayedByDate = {};
  sessions.forEach(({ timestamp }) => {
    const day = new Date(timestamp).getDay();
    if (gamesPlayedByDay[day]) {
      gamesPlayedByDay[day]++;
    } else {
      gamesPlayedByDay[day] = 1;
    }
  });
  for (let i = 0; i < 7; i++) {
    if (!gamesPlayedByDay[i]) gamesPlayedByDay[i] = 0;
  }
  for (const day in gamesPlayedByDay) {
    gamesPlayedByDay[day] /= Math.max(numberOfDays / 7, 1);
  }
  const res = [];
  for (const [x, y] of Object.entries(gamesPlayedByDay)) {
    res.push({ x: parseInt(x) + 1, y });
  }
  res.sort((a, b) => {
    return a.x - b.x;
  });

  return res;

  // res.map(({ x, y }: any) => ({
  //   x: weekDays[x],
  //   y,
  // }));
}
function getAverageGamesPerHour(sessions: Session[]) {
  const gamesPlayedByHour: GamesPlayedByDate = {};
  sessions.forEach(({ timestamp }) => {
    const hour = new Date(timestamp).getHours();
    if (gamesPlayedByHour[hour]) {
      gamesPlayedByHour[hour]++;
    } else {
      gamesPlayedByHour[hour] = 1;
    }
  });
  for (let i = 0; i < 24; i++) {
    if (!gamesPlayedByHour[i]) gamesPlayedByHour[i] = 0;
  }
  for (const hour in gamesPlayedByHour) {
    gamesPlayedByHour[hour] /= 24;
  }
  // console.log(Object.values(gamesPlayedByHour).reduce((acc, x) => acc + x, 0));
  const res = [];
  for (const [x, y] of Object.entries(gamesPlayedByHour)) {
    res.push({ x: parseInt(x), y });
  }
  res.sort((a, b) => {
    return a.x - b.x;
  });
  return res;
}
function getGamesPlayedData(sessions: Session[]) {
  const gamesPlayedByDate: GamesPlayedByDate = {};
  sessions.forEach(({ timestamp }) => {
    const date = new Date(timestamp.split("T")[0]).getTime();
    if (gamesPlayedByDate[date]) {
      gamesPlayedByDate[date]++;
    } else {
      gamesPlayedByDate[date] = 1;
    }
  });
  const gamesPlayedData: GamesPlayedData[] = [];
  let startDate = Number.MAX_VALUE;
  let endDate = 0;

  Object.keys(gamesPlayedByDate).forEach((key) => {
    const y = parseInt(key);
    if (y < startDate) startDate = y;
    else if (y > endDate) endDate = y;
  });
  const numberOfDays = (endDate - startDate) / 86400000 + 1;
  for (let i = 0; i < numberOfDays; i++) {
    if (!gamesPlayedByDate[startDate + i * 86400000])
      gamesPlayedByDate[startDate + i * 86400000] = 0;
  }
  for (const [x, y] of Object.entries(gamesPlayedByDate)) {
    gamesPlayedData.push({ x: parseInt(x), y });
  }
  gamesPlayedData.sort((a, b) => {
    return a.x - b.x;
  });
  // const startDate = gamesPlayedData[0].x;
  // const endDate = gamesPlayedData[gamesPlayedData.length - 1].x;
  return gamesPlayedData;
}
export type Session = {
  player_count: number;
  timestamp: string;
  local_timestamp?: string;
  duration: number;
  score: number;
  won_game: boolean;
  difficulty: string;
  game: string;
  level: string;
  credits?: number;
};

type GamesPlayedByDate = {
  [key: number]: number;
};

type GamesPlayedData = {
  x: number;
  y: number;
};

function round(x: number | null) {
  if (x === null) return null;
  return Math.round(x * 100) / 100;
}
const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const hoursTicks = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23,
];
